import {createStyles} from '@mantine/core';
import React, {ReactNode} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useHoverState from 'ui-library/hooks/useHoverState';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

const useStyles = createStyles((theme) => ({
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    borderRadius: 4,
    background: 'none',
    border: 'none',
    padding: 0,
  },
  enabledButton: {
    cursor: 'pointer',
  },
  disabledButton: {
    cursor: 'not-allowed',
  },
  icon: {
    color: theme.colors.blue[5],
    fontSize: 12,
  },
}));

interface SidebarIconButtonProps {
  tooltip: string;
  onClick: () => void;
  ariaLabel: string;
  dataTestComponent: string;
  disabled?: boolean;
  /**
   * Shorthand: pass a BenchlingIcon name to render a standard blue icon.
   * For custom icon rendering (e.g. AddIcon with hover/disabled states), use `children` instead.
   */
  icon?: BenchlingIconName;
  children?: (isHovered: boolean) => ReactNode;
}

/** @package */
export default function SidebarIconButton({
  tooltip,
  onClick,
  ariaLabel,
  dataTestComponent,
  disabled = false,
  icon,
  children,
}: SidebarIconButtonProps): JSX.Element {
  const [isHovered, hoverProps] = useHoverState();
  const {classes, cx} = useStyles();
  return (
    <Tooltip text={tooltip} isDisabled={false}>
      <button
        type="button"
        className={cx(classes.button, disabled ? classes.disabledButton : classes.enabledButton)}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        aria-label={ariaLabel}
        {...hoverProps}
        {...getTestProps({
          component: dataTestComponent,
          element: 'root',
        })}
      >
        {icon != null ? <BenchlingIcon icon={icon} className={classes.icon} /> : children?.(isHovered)}
      </button>
    </Tooltip>
  );
}
